"use client";
import Image from "next/image";
import { useState, useEffect } from "react";
import { Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import LanguageSwitcher from '../LanguageSwitcher';

export default function Navbar() {
  const t = useTranslations('Navigation');
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

  useEffect(() => {
    // Trigger animation after component mounts
    setTimeout(() => setIsLoaded(true), 100);
  }, []);

  const navigationItems = [
    {
      name: t('services'),
      href: "#",
      dropdown: [
        {
          name: t('moving_services'),
          href: "/",
          submenu: [
            { name: t('local_moving_vilnius'), href: "/local-moving-vilnius" },
            { name: t('international_moving'), href: "/international-moving" },
            { name: t('intercity_moving'), href: "/intercity-moving" },
            { name: t('apartment_moving'), href: "/apartment-moving" },
            { name: t('house_moving'), href: "/house-moving" },
            { name: t('office_moving'), href: "/office-moving" },
            { name: t('shop_moving'), href: "/shop-moving" },
            { name: t('warehouse_moving'), href: "/warehouse-moving" },
          ]
        },
        {
          name: t('transportation_services'),
          href: "/transportation-services",
          submenu: [
            { name: t('item_transport'), href: "/transportation-services/item-transport" },
            { name: t('furniture_transport'), href: "/transportation-services/furniture-transport" },
            { name: t('equipment_transport'), href: "/transportation-services/equipment-transport" },
            { name: t('piano_moving'), href: "/transportation-services/piano-moving" },
            { name: t('oversized_cargo_transport'), href: "/transportation-services/oversized-cargo-transport" },
          ]
        },
        {
          name: t('crane_services'),
          href: "/fiskars",
          submenu: [
            { name: t('garage_transport'), href: "/fiskars/garage-transport" },
            { name: t('modular_house_transport'), href: "/fiskars/modular-house-transport" },
            { name: t('construction_material_transport'), href: "/fiskars/construction-material-transport" },
            { name: t('timber_transport'), href: "/fiskars/timber-transport" },
            { name: t('metal_structure_transport'), href: "/fiskars/metal-structure-transport" },
            { name: t('installation_works'), href: "/fiskars/installation-works" },
          ]
        },
        {
          name: t('disposal_services'),
          href: "/disposal",
          submenu: [
            { name: t('old_furniture_removal'), href: "/disposal/old-furniture-removal" },
            { name: t('wood_waste_removal'), href: "/disposal/wood-waste-removal" },
            { name: t('plastic_waste_removal'), href: "/disposal/plastic-waste-removal" },
            { name: t('appliance_removal'), href: "/disposal/appliance-removal" },
            { name: t('bulky_waste_removal'), href: "/disposal/bulky-waste-removal" },
            { name: t('construction_waste_removal'), href: "/disposal/construction-waste-removal" },
          ]
        },
      ]
    },
    { name: t('about'), href: "/about" },
    { name: t('contacts'), href: "/contacts" },
  ];

  const [dropdownTimeout, setDropdownTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleDropdownEnter = (itemName: string) => {
    // Clear any existing timeout
    if (dropdownTimeout) {
      clearTimeout(dropdownTimeout);
      setDropdownTimeout(null);
    }
    setActiveDropdown(itemName);
  };

  const handleDropdownLeave = () => {
    // Add a reasonable delay before closing dropdown for good UX
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 400); // 400ms delay - gives users time to move cursor
    setDropdownTimeout(timeout);
  };

  return (
    <>
      {/* Backdrop blur overlay when dropdown is active */}
      {activeDropdown && (
        <div 
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(0,0,0,0.2)",
            zIndex: 999,
            animation: "fadeInBackdrop 0.3s ease-out",
          }}
        />
      )}
      
      <nav style={{
        position: "fixed",
        top: isLoaded ? "21px" : "-100px",
        left: "24px",
        right: "24px",
        height: "70px",
        borderRadius: "15px",
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(20px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 40px",
        zIndex: 1001,
        opacity: isLoaded ? 1 : 0,
        transition: "top 0.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
      }}>
        {/* Logo */}
        <Link href="/" style={{ 
          height: "50px", 
          borderRadius: "8px",
          overflow: "hidden",
          position: "relative",
          flex: "0 0 auto",
          opacity: isLoaded ? 1 : 0,
          transform: isLoaded ? "translateX(0)" : "translateX(-30px)",
          transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.2s",
          display: "block",
        }}>
          <Image 
            src="/images/mesjaucia-logo.png" 
            alt="Mes Jau Čia Logo" 
            width={150}
            height={50}
            style={{ objectFit: "contain" }}
          />
        </Link>

        {/* Mobile Hamburger Menu */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? "Close mobile menu" : "Open mobile menu"}
          aria-expanded={isMobileMenuOpen}
          style={{
            display: "none",
            flexDirection: "column",
            gap: "4px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "8px",
            zIndex: 1004, 
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? "translateX(0)" : "translateX(30px)",
            transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.3s",
          }}
          className="mobile-hamburger"
        >
          <span style={{
            width: "25px",
            height: "2px",
            background: "#fff",
            borderRadius: "2px",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: isMobileMenuOpen ? "rotate(45deg) translateY(6px)" : "rotate(0deg)",
          }} />
          <span style={{
            width: "25px",
            height: "2px",
            background: "#fff",
            borderRadius: "2px",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            opacity: isMobileMenuOpen ? 0 : 1,
          }} />
          <span style={{
            width: "25px",
            height: "2px",
            background: "#fff",
            borderRadius: "2px",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: isMobileMenuOpen ? "rotate(-45deg) translateY(-6px)" : "rotate(0deg)",
          }} />
        </button>

        {/* Nav Links - Centered */}
        <div className="desktop-nav-links" style={{ 
          display: "flex", 
          gap: "32px",
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          opacity: isLoaded ? 1 : 0,
          transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.4s",
        }}>
          {navigationItems.map((item, index) => (
            <div 
              key={item.name}
              style={{ position: "relative" }}
              onMouseEnter={() => item.dropdown && handleDropdownEnter(item.name)}
              onMouseLeave={handleDropdownLeave}
            >
              <Link 
                href={item.href} 
                style={{ 
                  color: "#fff", 
                  fontWeight: 500, 
                  fontSize: "15px",
                  textDecoration: "none",
                  opacity: isLoaded ? 1 : 0,
                  transform: isLoaded ? "translateY(0)" : "translateY(-15px)",
                  transition: `all 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${0.5 + (index * 0.1)}s`,
                  position: "relative",
                  padding: "8px 0",
                  overflow: "hidden",
                  display: "block",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#B51F24";
                  e.currentTarget.style.transform = "translateY(-1px)";
                  
                  const underline = e.currentTarget.querySelector('.nav-underline') as HTMLElement;
                  if (underline) {
                    underline.style.width = "100%";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#fff";
                  e.currentTarget.style.transform = "translateY(0)";
                  
                  const underline = e.currentTarget.querySelector('.nav-underline') as HTMLElement;
                  if (underline) {
                    underline.style.width = "0%";
                  }
                }}
              >
                {item.name}
                <span 
                  className="nav-underline"
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: "0",
                    height: "2px",
                    width: "0%",
                    background: "linear-gradient(90deg, #B51F24, #FF4E50)",
                    transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    borderRadius: "1px",
                  }}
                />
              </Link>


            </div>
          ))}
        </div>

        {/* Dropdown - positioned independently with navbar styling - Always rendered for SEO */}
        <div 
          style={{
            position: "fixed",
            top: "85px", // Slight overlap with navbar to create seamless transition
            left: "24px", // Match navbar margins
            right: "24px", // Match navbar margins
            background: "rgba(0,0,0,0.6)", // Match navbar background
            backdropFilter: "blur(20px)",
            padding: "46px 40px 40px 40px", // Extra top padding to account for overlap
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "15px", // Match navbar border radius
            zIndex: 1000,
            overflow: "hidden",
            minHeight: "400px",
            // SEO-friendly visibility control
            opacity: activeDropdown ? 1 : 0,
            visibility: activeDropdown ? "visible" : "hidden",
            transform: activeDropdown ? "translateY(0)" : "translateY(-10px)",
            transition: "opacity 0.3s ease-out, visibility 0.3s ease-out, transform 0.3s ease-out",
            pointerEvents: activeDropdown ? "auto" : "none",
          }}
          onMouseEnter={() => activeDropdown && handleDropdownEnter(activeDropdown)}
          onMouseLeave={handleDropdownLeave}
        >
          {/* Services Dropdown */}
          <div 
            style={{
              maxWidth: "1400px",
              margin: "0 auto",
              display: activeDropdown === t('services') ? "grid" : "none",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "40px",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            {navigationItems.find(item => item.name === t('services'))?.dropdown?.map((subItem) => (
              <div key={subItem.name} style={{ marginBottom: "20px" }}>
                <Link
                  href={subItem.href}
                  style={{
                    display: "block",
                    color: "#B51F24",
                    padding: "16px 0 12px 0",
                    fontSize: "18px",
                    fontWeight: "600",
                    textDecoration: "none",
                    transition: "all 0.2s ease",
                    borderBottom: "2px solid rgba(181, 31, 36, 0.3)",
                    marginBottom: "16px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#FF4E50";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#B51F24";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {subItem.name}
                </Link>
                {subItem.submenu && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {subItem.submenu.map((subSubItem) => (
                      <Link
                        key={subSubItem.name}
                        href={subSubItem.href}
                        style={{
                          display: "block",
                          color: "rgba(255,255,255,0.8)",
                          padding: "10px 16px",
                          fontSize: "14px",
                          textDecoration: "none",
                          transition: "all 0.2s ease",
                          borderRadius: "8px",
                          border: "1px solid transparent",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = "#fff";
                          e.currentTarget.style.backgroundColor = "rgba(181, 31, 36, 0.1)";
                          e.currentTarget.style.borderColor = "rgba(181, 31, 36, 0.3)";
                          e.currentTarget.style.transform = "translateX(8px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = "rgba(255,255,255,0.8)";
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.borderColor = "transparent";
                          e.currentTarget.style.transform = "translateX(0)";
                        }}
                      >
                        • {subSubItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Desktop Right Side Elements */}
        <div className="desktop-right-elements" style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          opacity: isLoaded ? 1 : 0,
          transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.5s",
        }}>
          {/* Language Switcher */}
          <div className="desktop-language-switcher">
            <LanguageSwitcher />
          </div>

          {/* Contact Button */}
          <Link href="#" style={{
            background: "linear-gradient(90deg, #B31217 0%,rgb(129, 0, 2) 100%)",
            color: "#fff",
            padding: "10px 16px",
            fontSize: "14px",
            fontWeight: 600,
            borderRadius: "8px",
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(179, 18, 23, 0.4)",
            whiteSpace: "nowrap",
          }}>
            <span style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 100%)",
              transform: "translateX(-100%)",
              transition: "transform 0.5s ease",
            }}
            />
            <span style={{ position: "relative", zIndex: 1 }}>{t('get_in_touch')}</span>
          </Link>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div style={{
          position: "fixed",
          top: "0",
          left: "0",
          right: "0",
          bottom: "0",
          background: "linear-gradient(135deg, #000000 0%, #1a1a1a 100%)",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          paddingTop: "120px",
          overflowY: "auto",
          animation: "mobileMenuSlide 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        className="mobile-menu-overlay"
        >
          {/* Mobile Language Switcher */}
          <div style={{
            marginBottom: "30px",
            opacity: 0,
            transform: "translateY(20px)",
            animation: "mobileItemSlide 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.1s forwards",
          }}>
            <LanguageSwitcher />
          </div>

          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
            width: "100%",
            maxWidth: "320px",
            padding: "20px 0 40px"
          }}>
            {navigationItems.map((item, index) => (
              <div key={item.name} style={{ width: "100%" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "0 20px",
                  }}
                >
                  <Link
                    href={item.href}
                    style={{
                      color: "#fff",
                      fontSize: "18px",
                      fontWeight: "500",
                      textDecoration: "none",
                      padding: "12px 0",
                      opacity: 0,
                      transform: "translateY(20px)",
                      animation: `mobileItemSlide 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${(index + 1) * 0.1 + 0.2}s forwards`,
                      flex: 1,
                      borderBottom: "1px solid rgba(255,255,255,0.1)",
                    }}
                    onClick={() => {
                      if (!item.dropdown) setIsMobileMenuOpen(false);
                    }}
                  >
                    {item.name}
                  </Link>
                  {item.dropdown && (
                    <button
                      onClick={() => setActiveSubmenu(activeSubmenu === item.name ? null : item.name)}
                      aria-label={`Toggle ${item.name} submenu`}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#fff",
                        fontSize: "16px",
                        padding: "12px 8px",
                        cursor: "pointer",
                        transform: activeSubmenu === item.name ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.3s ease",
                        opacity: 0,
                        animation: `mobileItemSlide 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${(index + 1) * 0.1 + 0.2}s forwards`,
                      }}
                    >
                      ↓
                    </button>
                  )}
                </div>
                
                {item.dropdown && activeSubmenu === item.name && (
                  <div style={{
                    marginTop: "10px",
                    marginBottom: "10px",
                    display: "block",
                    background: "rgba(255,255,255,0.05)",
                    padding: "20px",
                    borderRadius: "12px",
                    borderLeft: "4px solid #B51F24",
                    animation: "mobileSubmenuSlide 0.3s ease-out",
                    margin: "10px 20px",
                    backdropFilter: "blur(10px)",
                    maxHeight: "60vh",
                    overflowY: "auto",
                  }}>
                    {item.dropdown.map((subItem, subIndex) => (
                      <div key={subItem.name} style={{ marginBottom: "16px" }}>
                        <Link
                          href={subItem.href}
                          style={{
                            display: "block",
                            color: "#B51F24",
                            fontSize: "16px",
                            fontWeight: "600",
                            textDecoration: "none",
                            padding: "12px 0 8px 0",
                            transition: "color 0.2s ease",
                            borderBottom: "1px solid rgba(255,255,255,0.1)",
                          }}
                          onClick={() => setIsMobileMenuOpen(false)}
                          onTouchStart={(e) => {
                            e.currentTarget.style.color = "#FF4E50";
                          }}
                          onTouchEnd={(e) => {
                            setTimeout(() => {
                              e.currentTarget.style.color = "#B51F24";
                            }, 150);
                          }}
                        >
                          {subItem.name}
                        </Link>
                        {subItem.submenu && (
                          <div style={{ marginLeft: "16px", marginTop: "8px" }}>
                            {subItem.submenu.map((subSubItem) => (
                              <Link
                                key={subSubItem.name}
                                href={subSubItem.href}
                                style={{
                                  display: "block",
                                  color: "rgba(255,255,255,0.8)",
                                  fontSize: "14px",
                                  textDecoration: "none",
                                  padding: "8px 0",
                                  transition: "color 0.2s ease",
                                }}
                                onClick={() => setIsMobileMenuOpen(false)}
                                onTouchStart={(e) => {
                                  e.currentTarget.style.color = "#ffffff";
                                }}
                                onTouchEnd={(e) => {
                                  setTimeout(() => {
                                    e.currentTarget.style.color = "rgba(255,255,255,0.8)";
                                  }, 150);
                                }}
                              >
                                • {subSubItem.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            <Link
              href="#"
              style={{
                background: "linear-gradient(90deg, #B31217 0%,rgb(129, 0, 2) 100%)",
                color: "#fff",
                fontWeight: "600",
                fontSize: "16px",
                borderRadius: "12px",
                padding: "16px 32px",
                textDecoration: "none",
                marginTop: "30px",
                boxShadow: "0 6px 20px rgba(181, 31, 36, 0.4)",
                opacity: 0,
                transform: "translateY(20px)",
                animation: `mobileItemSlide 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${navigationItems.length * 0.1 + 0.4}s forwards`,
                textAlign: "center",
                minWidth: "200px",
              }}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('get_in_touch')}
            </Link>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeInDropdown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInBackdrop {
          from {
            opacity: 0;
            backdropFilter: blur(0px);
          }
          to {
            opacity: 1;
            backdropFilter: blur(8px);
          }
        }
        
        @keyframes mobileMenuSlide {
          from {
            opacity: 0;
            transform: scale(0.98);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes mobileItemSlide {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes mobileSubmenuSlide {
          from {
            opacity: 0;
            transform: translateY(-10px);
            max-height: 0;
          }
          to {
            opacity: 1;
            transform: translateY(0);
            max-height: 500px;
          }
        }
        
        @media (max-width: 1024px) {
          .mobile-hamburger {
            display: flex !important;
          }
          
          .desktop-nav-links, 
          .desktop-right-elements {
            display: none !important;
          }
        }
        
        @media (max-width: 1024px) {
          .desktop-nav-links, 
          .desktop-right-elements {
            display: none !important;
          }
          
          .mobile-hamburger {
            display: flex !important;
          }
        }
        
        @media (max-width: 768px) {
          nav {
            left: 12px !important;
            right: 12px !important;
            padding: 0 20px !important;
          }
          
          nav a img {
            width: 120px !important;
            height: 40px !important;
          }
        }
        
        @media (max-width: 480px) {
          nav {
            left: 8px !important;
            right: 8px !important;
            padding: 0 16px !important;
          }
          
          nav a img {
            width: 100px !important;
            height: 35px !important;
          }
          
          .mobile-hamburger span {
            width: 20px !important;
          }
          
          .mobile-menu-overlay {
            padding-top: 100px !important;
          }
        }
      `}</style>
    </>
  );
} 